import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { SiteService } from "@api/site.service";
import { filter, switchMap } from "rxjs/operators";
import { BehaviorSubject } from "rxjs";
import { FPCategoryVM, SiteVM } from "@models/backend-generated-models/models";
import { SiteStateService } from "@states/site-state.service";
import { MatDialog } from "@angular/material/dialog";
import { ModalReportsComponent } from "../../modal/modal-reports/modal-reports.component";
import { ModalLoggerHealthComponent } from "../../modal/modal-logger-health/modal-logger-health.component";
import { UserMenuService } from '@api/user-menu.service';
import { UserStateService } from '@states/user-state.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';


@Component({
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit {
  @ViewChildren("mainItem") divs: QueryList<ElementRef<HTMLDivElement>> = new QueryList<ElementRef<HTMLDivElement>>();

  mainItems: {
    id: ('I' | 'B' | 'O' | 'A')[],
    name: 'My Utilities' | 'Live Data' | 'My Reports and Operations',
    hoverColorHex: string,
    imageUrl: string;
    fpResourceCategories: BehaviorSubject<FPCategoryVM[]>;
  }[] = [
      {
        id: ['I'],
        name: 'My Utilities',
        hoverColorHex: '#5597A7',
        imageUrl: 'assets/images/svg/category_utility.svg',
        fpResourceCategories: new BehaviorSubject<FPCategoryVM[]>([])
      },
      {
        id: ['B', 'A'],
        name: 'Live Data',
        hoverColorHex: '#ED5E19',
        imageUrl: 'assets/images/svg/category_facility.svg',
        fpResourceCategories: new BehaviorSubject<FPCategoryVM[]>([])
      },
      {
        id: ['O'],
        name: 'My Reports and Operations',
        hoverColorHex: '#A0CC3B',
        imageUrl: 'assets/images/svg/category_reports.svg',
        fpResourceCategories: new BehaviorSubject<FPCategoryVM[]>([])
      }
    ];

  portalIds: string[] = [];

  allSiteCategories: FPCategoryVM[] = [];
  pinnedItems: FPCategoryVM[] = [];
  isSuperAdmin: boolean = false;

  constructor(
    private siteService: SiteService,
    private siteStateService: SiteStateService,
    public dialog: MatDialog,
    private userMenuService: UserMenuService,
    private cdr: ChangeDetectorRef,
    private userStateService: UserStateService
  ) { }

  reportViewerURL$ = this.userMenuService.getReportViewerURL$().pipe();

  ngOnInit(): void {
    this.loadPortalIds();

    console.log("Loaded portal IDs:", this.portalIds);

    // Force change detection
    this.cdr.detectChanges();

    // Load default dashboard
    this.loadSiteData();

    this.userStateService.currentUser$.subscribe(user => {
      const role = user && user.role ? user.role.toLowerCase() : '';
      this.isSuperAdmin = role.includes('superadmin');

      this.loadPinnedItems();
      this.loadFpItemsIntoMainItems(this.allSiteCategories);
    });
  }

  // Loads the portal ID(s) from local storage.
  private loadPortalIds(): void {
    const storedPortalId = localStorage.getItem('portalId');
    if (storedPortalId) {
      this.portalIds = storedPortalId.split(',').map(id => id.trim());
      console.log("Updated portalIds:", this.portalIds);
    }
  }

  private loadSiteData(): void {
    this.siteStateService.selectedSite$.pipe(
      filter(site => site !== null),
      switchMap((site) => {
        return this.siteService.deepLoadSite$((site as SiteVM).siteId!);
      })
    ).subscribe({
      next: (sites) => {
        console.log("Fetched site data:", sites);
        this.allSiteCategories = sites;
        this.loadFpItemsIntoMainItems(sites);
        this.hideAllFpItems();
        this.loadPinnedItems();
      }
    });
  }

  private loadFpItemsIntoMainItems(fpCategories: FPCategoryVM[]) {
    this.mainItems[0].fpResourceCategories.next(
      this.sortCategories(
        fpCategories.filter(fpc => fpc.locationId === 'I'),
        'I'
      )
    );
    this.mainItems[1].fpResourceCategories.next(
      this.sortCategories(
        fpCategories.filter(fpc => fpc.locationId === 'B' || fpc?.locationId === 'A'),
        'B_A'
      )
    );
    this.mainItems[2].fpResourceCategories.next(
      this.sortCategories(
        [
          ...fpCategories.filter(fpc => fpc.locationId === 'O'),
          ...this.feedReportsWithExtraStaticFpCategories()
        ],
        'O'
      )
    );
  }

  private feedReportsWithExtraStaticFpCategories(): FPCategoryVM[] {
    const list: FPCategoryVM[] = [
      {
        categoryName: 'Event History',
        fpicon: { iconPath: '19-EventHistory.svg' },
        report: { reportUrl: 'rptEventHistoryChart' },
        showFormat: 'YQMWD',
        defaultFormat: '1Y',
        order: 1
      },
      {
        categoryName: 'Alarm Indicators',
        fpicon: { iconPath: '22-PolicyIndicator.svg' },
        report: { reportUrl: 'rptPolicyKPIStatus' },
        showFormat: 'D',
        defaultFormat: '0D',
        order: 2
      }
    ];

    if (this.isSuperAdmin) {
      list.push({
        categoryName: 'Maintenance',
        fpicon: { iconPath: '21-Diagnostic.svg' },
        report: { reportUrl: 'rptServiceStatus' },
        showFormat: '',
        defaultFormat: '',
        order: 3
      });
    }

    return list;
  }

  getFpItemIcon(iconPath: string | undefined): string {
    if (!iconPath) return '';
    const realIconPath = iconPath.replace('.png', '.svg');
    return `assets/images/svg/Items/${realIconPath}`;
  }

  showFpItems(mainItem: HTMLDivElement) {
    this.hideAllFpItems();
    mainItem.querySelector(".categories")?.classList.remove('hide');
  }

  private hideAllFpItems() {
    this.divs.forEach(elRef => {
      const div = elRef.nativeElement;
      div.querySelector('.categories')?.classList.add('hide');
    });
  }

  openReportModal(fpResourceCategory: FPCategoryVM, category: HTMLDivElement) {
    if (category.parentElement?.classList.contains('hide')) return;

    if (fpResourceCategory.report?.reportUrl === 'rptServiceStatus') {
      if (!this.isSuperAdmin) return;
      this.dialog.open(ModalLoggerHealthComponent, {
        width: '95vw',
        maxWidth: '1350px',
        panelClass: 'logger-health-dialog-panel'
      });
      return;
    }

    this.dialog.open(ModalReportsComponent, {
      width: '90vw',
      data: {
        category: fpResourceCategory,
        url: this.reportViewerURL$
      },
    });
  }





  private getPinsStorageKey(): string {
    const userId = this.getUserId();
    const siteId = this.siteStateService.selectedSiteSource.getValue()?.siteId;
    return `carbon_dashboard_pins_user_${userId || 'anonymous'}_site_${siteId || 'none'}`;
  }

  private getUserId(): string | null {
    const jwtUser = this.userStateService.currentUserSource.getValue();
    if (!jwtUser) return null;
    return jwtUser["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] || null;
  }

  loadPinnedItems(): void {
    const key = this.getPinsStorageKey();
    const pinnedStr = localStorage.getItem(key);
    if (!pinnedStr) {
      this.pinnedItems = [];
      this.cdr.markForCheck();
      return;
    }

    try {
      const pinnedKeys: string[] = JSON.parse(pinnedStr);
      const staticCats = this.feedReportsWithExtraStaticFpCategories();
      const combined = [...this.allSiteCategories, ...staticCats];

      this.pinnedItems = combined.filter(cat => {
        const catKey = cat.fpcategoryId ? cat.fpcategoryId.toString() : cat.categoryName;
        return catKey && pinnedKeys.includes(catKey);
      });
    } catch (e) {
      console.error("Error parsing pinned items", e);
      this.pinnedItems = [];
    }
    this.cdr.markForCheck();
  }

  togglePin(cat: FPCategoryVM, event: Event): void {
    event.stopPropagation();
    const key = this.getPinsStorageKey();
    const catKey = cat.fpcategoryId ? cat.fpcategoryId.toString() : cat.categoryName;
    if (!catKey) return;

    let pinnedKeys: string[] = [];
    const pinnedStr = localStorage.getItem(key);
    if (pinnedStr) {
      try {
        pinnedKeys = JSON.parse(pinnedStr);
      } catch (e) {
        console.error("Error parsing pinned keys", e);
      }
    }

    const index = pinnedKeys.indexOf(catKey);
    if (index > -1) {
      pinnedKeys.splice(index, 1);
    } else {
      pinnedKeys.push(catKey);
    }

    localStorage.setItem(key, JSON.stringify(pinnedKeys));
    this.loadPinnedItems();
  }

  isPinned(cat: FPCategoryVM): boolean {
    const catKey = cat.fpcategoryId ? cat.fpcategoryId.toString() : cat.categoryName;
    if (!catKey) return false;
    return this.pinnedItems.some(item => {
      const itemKey = item.fpcategoryId ? item.fpcategoryId.toString() : item.categoryName;
      return itemKey === catKey;
    });
  }

  openPinnedReport(fpResourceCategory: FPCategoryVM) {
    if (fpResourceCategory.report?.reportUrl === 'rptServiceStatus') {
      if (!this.isSuperAdmin) return;
      this.dialog.open(ModalLoggerHealthComponent, {
        width: '95vw',
        maxWidth: '1350px',
        panelClass: 'logger-health-dialog-panel'
      });
      return;
    }

    this.dialog.open(ModalReportsComponent, {
      width: '90vw',
      data: {
        category: fpResourceCategory,
        url: this.reportViewerURL$
      },
    });
  }

  private getOrderStorageKey(): string {
    const userId = this.getUserId();
    const siteId = this.siteStateService.selectedSiteSource.getValue()?.siteId;
    return `carbon_dashboard_order_user_${userId || 'anonymous'}_site_${siteId || 'none'}`;
  }

  private getCatKey(cat: FPCategoryVM): string {
    return cat.fpcategoryId ? cat.fpcategoryId.toString() : (cat.categoryName || '');
  }

  private sortCategories(categories: FPCategoryVM[], sectionKey: string): FPCategoryVM[] {
    const orderKey = this.getOrderStorageKey();
    const orderStr = localStorage.getItem(orderKey);
    if (orderStr) {
      try {
        const orderMap = JSON.parse(orderStr);
        const sectionOrder: string[] = orderMap[sectionKey];
        if (Array.isArray(sectionOrder) && sectionOrder.length > 0) {
          return [...categories].sort((a, b) => {
            const keyA = this.getCatKey(a);
            const keyB = this.getCatKey(b);
            const indexA = sectionOrder.indexOf(keyA);
            const indexB = sectionOrder.indexOf(keyB);

            if (indexA > -1 && indexB > -1) {
              return indexA - indexB;
            }
            if (indexA > -1) return -1;
            if (indexB > -1) return 1;

            return (a.order || 0) - (b.order || 0);
          });
        }
      } catch (e) {
        console.error("Error sorting categories", e);
      }
    }
    return [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  drop(event: CdkDragDrop<any>, id: ('I' | 'B' | 'O' | 'A')[]) {
    const targetItem = this.mainItems.find(mainItem => mainItem.id.includes(id[0]));
    if (!targetItem) return;

    const targetCategories = [...targetItem.fpResourceCategories.getValue()];
    moveItemInArray(targetCategories, event.previousIndex, event.currentIndex);
    targetItem.fpResourceCategories.next(targetCategories);

    const sectionKey = id.join('_');
    const orderKey = this.getOrderStorageKey();
    let orderMap: Record<string, string[]> = {};
    const orderStr = localStorage.getItem(orderKey);
    if (orderStr) {
      try {
        orderMap = JSON.parse(orderStr);
      } catch (e) {
        console.error("Error parsing dashboard order map on drop", e);
      }
    }
    orderMap[sectionKey] = targetCategories.map(cat => this.getCatKey(cat));
    localStorage.setItem(orderKey, JSON.stringify(orderMap));
  }
}
